// AUTO-GENERATED — DO NOT EDIT THIS FILE DIRECTLY.
//
// Synced from WalletProvider.jsx
// by scripts/sync_wallet_app.sh on 2026-07-17T08:09:17Z.
//
// To make changes:
//   1. Edit react/_shared/wallet-app/WalletProvider.jsx
//   2. Run ./scripts/sync_wallet_app.sh
//
// Editing this copy in place will be overwritten on next sync.
//
// ---------------------------------------------------------------

// WalletProvider.jsx
//
// One-line provider for the Bankcoin customer apps. Wraps:
//   - WagmiProvider          (wallet connection state)
//   - QueryClientProvider    (react-query — used by wagmi internals + apps)
//   - RainbowKitProvider     (the connect-button UI)
//   - RoleProvider           (customer-side RBAC context, see wallet-rbac/)
//
// Plus the auto-sign-in effect: on `useAccount().status === "connected"`,
// runs authenticateWallet() with the wallet's signMessage adapter.
//
// Usage (in each app's main.jsx):
//   <WalletProvider appName="Mint — Bankcoin Capital">
//     <App />
//   </WalletProvider>
//
// Wallet connector set (fixed 2026-07-14 — see CLAUDE.md "WalletConnect/Reown"
// rule): this MUST use the shared `buildWalletConnectors()` (auto-discovers
// every wallet the installed RainbowKit exports — the FULL list), never
// RainbowKit's bare `getDefaultConfig()`, which only offers its own curated
// ~4-wallet default set. bridge.bankcoin.capital shipped with the bare
// getDefaultConfig() call and its Connect Wallet modal only showed ~4
// wallets — the same defect this rule exists to prevent. `wallet_connectors.js`
// is copied in from the SAME canonical file the wagmi.js-based apps use
// (react/_shared/wallet/wallet_connectors.js, via scripts/sync_wallet_app.sh)
// so there is exactly one implementation of "the full wallet list", reused,
// not forked.
//
// projectId is sourced ONLY from react/_shared/config/clients.json (via the
// generated src/generated/clients.js, scripts/sync_frontend_config.sh) —
// never an env var, never a literal. Every consumer of this module
// (bridge/mint/fx-bankcoin-web) is a Bankcoin-company app; the generated
// constant always resolves to the Bankcoin project id, never CryptoDeposit's.

import React, { useEffect, useState } from 'react';
import { WagmiProvider, createConfig, createStorage, useAccount, useWalletClient } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RainbowKitProvider, darkTheme } from '@rainbow-me/rainbowkit';
import '@rainbow-me/rainbowkit/styles.css';

import { APP_CHAINS, buildTransports } from './chains';
import { authenticateWallet, clearAuth } from './useAuthToken';
import { RoleProvider } from './wallet-rbac/RoleContext';
import { buildWalletConnectors } from './wallet_connectors';
import { WALLETCONNECT_PROJECT_ID } from '../../generated/clients';

const PROJECT_ID = WALLETCONNECT_PROJECT_ID;

const queryClient = new QueryClient();

// Memoise the wagmi config so it isn't rebuilt on every parent re-render.
let _wagmiConfig = null;
function getWagmiConfig(appName) {
  if (_wagmiConfig) return _wagmiConfig;

  const connectors = buildWalletConnectors({
    projectId: PROJECT_ID,
    appName: appName || 'Bankcoin Capital',
  });

  const browserStorage =
    typeof window !== 'undefined' && window.localStorage ? window.localStorage : undefined;

  // Namespaced, versioned storage key — switching the connector set (as this
  // 2026-07-14 fix does) can otherwise leave a persisted session pointing at a
  // connector id that no longer exists, producing a "connected but dead"
  // wallet on reload. Bump the version whenever the connector set or
  // projectId changes in a way that should invalidate old sessions (same
  // pattern as react/bankcoin-web/src/wagmi.js).
  _wagmiConfig = createConfig({
    chains: APP_CHAINS,
    connectors,
    transports: buildTransports(),
    ssr: false,
    storage: createStorage({ storage: browserStorage, key: 'walletapp.wagmi.v1' }),
  });
  return _wagmiConfig;
}

// Internal: the auto-auth effect. Mounted inside the WagmiProvider so
// useAccount/useWalletClient resolve.
function AutoAuth({ onAuthFailed, autoAuthenticate = true }) {
  const { address, isConnected, status } = useAccount();
  const { data: walletClient } = useWalletClient();

  useEffect(() => {
    // Public / non-custodial apps (e.g. the bridge) MUST opt out: they have no
    // /auth/wallet endpoint, so a SIWE sign fails, the token never stores, and
    // walletClient identity-churn re-fires this effect → an endless "sign this
    // timestamp" loop (2026-07-15 bridge incident). Only sign in where a JWT is
    // actually consumed.
    if (!autoAuthenticate) return;
    if (status === 'connecting' || status === 'reconnecting') return;
    if (status === 'disconnected' || !isConnected || !address) {
      clearAuth();
      return;
    }
    if (!walletClient) return;

    let cancelled = false;
    const sign = async (args) =>
      walletClient.signMessage({ account: address, message: args.message });
    authenticateWallet(address, sign).catch((err) => {
      if (!cancelled && typeof onAuthFailed === 'function') {
        onAuthFailed(err);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [address, isConnected, walletClient, status, onAuthFailed, autoAuthenticate]);

  return null;
}

/**
 * The top-level provider. Drop once at the root of each Track-C app.
 *
 * Props:
 *   appName            (string)   - WalletConnect-visible app name
 *   onAuthFailed       (err)=>{}  - optional callback when wallet sign-in fails
 *   initialRole        (string)   - RBAC seed role, default 'signer'
 *   children           (ReactNode)
 */
export function WalletProvider({
  appName = 'Bankcoin Capital',
  onAuthFailed,
  initialRole = 'signer',
  autoAuthenticate = true,  // false for public/non-custodial apps (bridge) — no SIWE
  children,
}) {
  const [, setAuthError] = useState(null);
  const handleAuthFailed = (err) => {
    setAuthError(err);
    if (typeof onAuthFailed === 'function') onAuthFailed(err);
  };

  return (
    <WagmiProvider config={getWagmiConfig(appName)}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider
          theme={darkTheme({
            accentColor: '#7C3AED',
            accentColorForeground: '#FFFFFF',
            borderRadius: 'large',
            fontStack: 'system',
            overlayBlur: 'small',
          })}
        >
          <RoleProvider initialRole={initialRole}>
            <AutoAuth onAuthFailed={handleAuthFailed} autoAuthenticate={autoAuthenticate} />
            {children}
          </RoleProvider>
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}

export default WalletProvider;
